'use client';

import { useRef, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import katex from 'katex';
import { useDialogLayer } from './useDialogLayer';
import 'katex/dist/katex.min.css';

// ─── Data ────────────────────────────────────────────────────────────────────

interface CheatItem {
  label: string;
  latex: string;
  /** Copy as display math $$...$$ instead of inline $...$ */
  display?: boolean;
}

interface CheatCategory {
  name: string;
  items: CheatItem[];
}

const CHEATSHEET: CheatCategory[] = [
  {
    name: 'Dasar',
    items: [
      { label: 'kali', latex: '\\times' },
      { label: 'bagi', latex: '\\div' },
      { label: 'plus-minus', latex: '\\pm' },
      { label: 'titik kali', latex: '\\cdot' },
      { label: 'tidak sama', latex: '\\neq' },
      { label: 'mendekati', latex: '\\approx' },
      { label: '≤', latex: '\\leq' },
      { label: '≥', latex: '\\geq' },
      { label: '≪', latex: '\\ll' },
      { label: '≫', latex: '\\gg' },
      { label: '∝', latex: '\\propto' },
    ],
  },
  {
    name: 'Pangkat & Akar',
    items: [
      { label: 'kuadrat', latex: 'x^2' },
      { label: 'pangkat n', latex: 'x^n' },
      { label: 'subskrip', latex: 'x_n' },
      { label: 'akar', latex: '\\sqrt{x}' },
      { label: 'akar ke-n', latex: '\\sqrt[n]{x}' },
      { label: 'invers', latex: 'x^{-1}' },
    ],
  },
  {
    name: 'Pecahan',
    items: [
      { label: 'a/b', latex: '\\frac{a}{b}' },
      { label: 'campuran', latex: '1\\frac{a}{b}' },
      { label: 'turunan', latex: '\\frac{d}{dx}', display: true },
      { label: 'parsial', latex: '\\frac{\\partial f}{\\partial x}', display: true },
    ],
  },
  {
    name: 'Huruf Yunani',
    items: [
      { label: 'α alpha', latex: '\\alpha' },
      { label: 'β beta', latex: '\\beta' },
      { label: 'γ gamma', latex: '\\gamma' },
      { label: 'Δ Delta', latex: '\\Delta' },
      { label: 'δ delta', latex: '\\delta' },
      { label: 'ε epsilon', latex: '\\epsilon' },
      { label: 'θ theta', latex: '\\theta' },
      { label: 'λ lambda', latex: '\\lambda' },
      { label: 'μ mu', latex: '\\mu' },
      { label: 'π pi', latex: '\\pi' },
      { label: 'σ sigma', latex: '\\sigma' },
      { label: 'Σ Sigma', latex: '\\Sigma' },
      { label: 'φ phi', latex: '\\phi' },
      { label: 'ω omega', latex: '\\omega' },
      { label: 'Ω Omega', latex: '\\Omega' },
    ],
  },
  {
    name: 'Kalkulus',
    items: [
      { label: 'tak hingga', latex: '\\infty' },
      { label: 'integral', latex: '\\int', display: true },
      { label: 'integral batas', latex: '\\int_a^b', display: true },
      { label: 'jumlah Σ', latex: '\\sum_{i=1}^{n}', display: true },
      { label: 'produk Π', latex: '\\prod_{i=1}^{n}', display: true },
      { label: 'limit', latex: '\\lim_{x \\to 0}', display: true },
      { label: '→ menuju', latex: '\\to' },
    ],
  },
  {
    name: 'Fungsi',
    items: [
      { label: 'sin', latex: '\\sin' },
      { label: 'cos', latex: '\\cos' },
      { label: 'tan', latex: '\\tan' },
      { label: 'log', latex: '\\log' },
      { label: 'ln', latex: '\\ln' },
      { label: 'log₁₀', latex: '\\log_{10}' },
      { label: 'exp', latex: 'e^x' },
    ],
  },
  {
    name: 'Himpunan',
    items: [
      { label: '∈ anggota', latex: '\\in' },
      { label: '∉ bukan', latex: '\\notin' },
      { label: '⊂ bagian', latex: '\\subset' },
      { label: '⊆ bagian=', latex: '\\subseteq' },
      { label: '∪ gabungan', latex: '\\cup' },
      { label: '∩ irisan', latex: '\\cap' },
      { label: '∅ kosong', latex: '\\emptyset' },
      { label: 'ℝ real', latex: '\\mathbb{R}' },
      { label: 'ℤ integer', latex: '\\mathbb{Z}' },
      { label: 'ℕ natural', latex: '\\mathbb{N}' },
    ],
  },
  {
    name: 'Geometri',
    items: [
      { label: '∠ sudut', latex: '\\angle' },
      { label: '∥ sejajar', latex: '\\parallel' },
      { label: '⊥ tegak lurus', latex: '\\perp' },
      { label: '△ segitiga', latex: '\\triangle' },
      { label: 'vektor', latex: '\\vec{v}' },
      { label: 'garis AB', latex: '\\overline{AB}' },
      { label: 'busur AB', latex: '\\widehat{AB}' },
      { label: '° derajat', latex: '^\\circ' },
      { label: '≅ kongruen', latex: '\\cong' },
      { label: '∼ sebangun', latex: '\\sim' },
    ],
  },
  {
    name: 'Matriks',
    items: [
      { label: 'matriks 2×2', latex: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}', display: true },
      { label: 'determinan', latex: '\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}', display: true },
      { label: 'T transpose', latex: 'A^T' },
      { label: 'invers', latex: 'A^{-1}' },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function renderMath(latex: string, display = false): string {
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: display,
      trust: true,
    });
  } catch {
    return latex;
  }
}

// ─── Item card ────────────────────────────────────────────────────────────────

function CheatCard({ item }: { item: CheatItem }) {
  const [copied, setCopied] = useState(false);

  const toCopy = item.display ? `$$${item.latex}$$` : `$${item.latex}$`;

  const handleCopy = () => {
    navigator.clipboard.writeText(toCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title={`Salin: ${toCopy}`}
      className="group relative flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40 transition-all text-center w-full"
    >
      {/* Rendered preview */}
      <div
        className="text-slate-900 leading-none"
        style={{ fontSize: '1rem' }}
        dangerouslySetInnerHTML={{ __html: renderMath(item.latex, item.display) }}
      />
      {/* LaTeX code */}
      <code className="text-[9px] text-slate-400 group-hover:text-teal-600 font-mono leading-none break-all">
        {toCopy}
      </code>
      {/* Copy feedback */}
      <span className={`absolute top-1 right-1 transition-opacity ${copied ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}>
        {copied
          ? <Check className="w-3 h-3 text-teal-600" />
          : <Copy className="w-3 h-3 text-slate-400" />
        }
      </span>
    </button>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export interface MathCheatsheetProps {
  open: boolean;
  onClose: () => void;
}

export function MathCheatsheet({ open, onClose }: MathCheatsheetProps) {
  const [activeTab, setActiveTab] = useState(CHEATSHEET[0].name);
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogLayer(open, onClose, panelRef);

  if (!open) return null;

  const active = CHEATSHEET.find(c => c.name === activeTab) ?? CHEATSHEET[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Referensi Simbol Math"
        tabIndex={-1}
        className="relative outline-none bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Referensi Simbol Math</h2>
            <p className="text-xs text-slate-400 mt-0.5">Klik simbol untuk menyalin ke clipboard, lalu tempel di editor</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Tutup"
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 pb-0 flex-wrap border-b border-slate-100">
          {CHEATSHEET.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setActiveTab(cat.name)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors whitespace-nowrap -mb-px ${
                activeTab === cat.name
                  ? 'border-teal-500 text-teal-700 bg-teal-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="overflow-y-auto flex-1 p-4">
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
            {active.items.map((item) => (
              <CheatCard key={item.latex} item={item} />
            ))}
          </div>

          {/* Usage tip */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong className="text-slate-700">Cara pakai:</strong>{' '}
              Klik simbol → disalin otomatis → tempel di editor dengan <kbd className="bg-white border border-slate-200 px-1 py-0.5 rounded text-[10px] font-mono">Ctrl+V</kbd>.
              Gunakan <code className="bg-white border border-slate-200 px-1 rounded font-mono">$...$</code> untuk math inline,{' '}
              <code className="bg-white border border-slate-200 px-1 rounded font-mono">$$...$$</code> untuk blok tersendiri.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
